import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2 } from "lucide-react";
import { API_BASE } from "@/constants/api";
import {
  areaCodeFromOutward,
  parseCoverageTokens,
  selectedAreaCodes,
  toggleAreaToken,
} from "@/utils/coverageAreas";

const MAP_HEIGHT_PX = 400;

type CoverageAreaMapProps = {
  value: string;
  onChange: (next: string) => void;
};

function styleForArea(selected: boolean): L.PathOptions {
  return selected
    ? {
        fillColor: "#29ffa9",
        color: "#29ffa9",
        weight: 0.8,
        fillOpacity: 0.45,
        lineJoin: "round",
      }
    : {
        fillColor: "#94a3b8",
        color: "#64748b",
        weight: 0.4,
        fillOpacity: 0.18,
        lineJoin: "round",
      };
}

function areaFromLayer(layer: L.Layer): string {
  const feature = (layer as L.Layer & { feature?: GeoJSON.Feature }).feature;
  return areaCodeFromOutward(String(feature?.properties?.outward_code ?? ""));
}

function clearLeafletId(el: HTMLElement) {
  delete (el as HTMLElement & { _leaflet_id?: number })._leaflet_id;
}

export default function CoverageAreaMap({ value, onChange }: CoverageAreaMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const geoLayerRef = useRef<L.GeoJSON | null>(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [loadError, setLoadError] = useState("");

  valueRef.current = value;
  onChangeRef.current = onChange;

  const selectedKey = useMemo(
    () => [...selectedAreaCodes(parseCoverageTokens(value))].sort().join("|"),
    [value],
  );
  const selected = useMemo(
    () => new Set(selectedKey === "" ? [] : selectedKey.split("|")),
    [selectedKey],
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let cancelled = false;
    let resizeObserver: ResizeObserver | null = null;

    const teardown = () => {
      resizeObserver?.disconnect();
      resizeObserver = null;
      geoLayerRef.current = null;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      clearLeafletId(el);
    };

    clearLeafletId(el);

    const map = L.map(el, {
      attributionControl: true,
      zoomControl: true,
      scrollWheelZoom: true,
      preferCanvas: true,
    }).setView([52.855, -1.474], 6);

    map.attributionControl.setPrefix("");
    map.attributionControl.addAttribution(
      'Districts Wikipedia/CC&nbsp;BY-SA via <a href="https://github.com/missinglink/uk-postcode-polygons">uk-postcode-polygons</a>',
    );

    mapRef.current = map;
    map.invalidateSize(true);
    resizeObserver = new ResizeObserver(() => map.invalidateSize(true));
    resizeObserver.observe(el);

    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/geo/postcode-districts-england`, {
          credentials: "omit",
        });
        if (!res.ok) {
          throw new Error(`Failed to load postcode geometry (${res.status})`);
        }
        const fc = (await res.json()) as GeoJSON.FeatureCollection;
        if (cancelled) return;

        const selectedNow = selectedAreaCodes(parseCoverageTokens(valueRef.current));
        const geoLayer = L.geoJSON(fc, {
          style(feature) {
            const area = areaCodeFromOutward(String(feature?.properties?.outward_code ?? ""));
            return styleForArea(selectedNow.has(area));
          },
          onEachFeature(feature, layer) {
            const outward = String(feature.properties?.outward_code ?? "");
            const area = areaCodeFromOutward(outward);
            layer.bindTooltip(area ? `${area} · ${outward}` : outward, {
              sticky: true,
              direction: "top",
              opacity: 0.94,
            });
            layer.on("click", () => {
              if (!area) return;
              onChangeRef.current(toggleAreaToken(valueRef.current, area));
            });
            layer.on("mouseover", () => {
              el.style.cursor = "pointer";
            });
            layer.on("mouseout", () => {
              el.style.cursor = "";
            });
          },
        }).addTo(map);

        geoLayerRef.current = geoLayer;
        const bounds = geoLayer.getBounds();
        if (bounds.isValid()) {
          map.fitBounds(bounds, { padding: [18, 18], maxZoom: 7 });
          map.setMaxBounds(bounds.pad(0.05));
        }
        map.invalidateSize(true);
        setLoadState("ready");
      } catch (e) {
        if (cancelled) return;
        setLoadState("error");
        setLoadError(e instanceof Error ? e.message : String(e));
      }
    })();

    return () => {
      cancelled = true;
      teardown();
    };
  }, []);

  useEffect(() => {
    const layer = geoLayerRef.current;
    if (!layer) return;
    layer.eachLayer((pathLayer) => {
      (pathLayer as L.Path).setStyle(styleForArea(selected.has(areaFromLayer(pathLayer))));
    });
  }, [selected]);

  return (
    <div
      className="relative overflow-hidden rounded-lg border border-white/10 bg-[#0a1628]"
      style={{ height: MAP_HEIGHT_PX }}
    >
      {loadState === "loading" && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-[#0a1628]/80 text-sm text-white/50">
          <Loader2 className="mr-2 h-5 w-5 animate-spin text-[#29ffa9]" />
          Loading coverage map…
        </div>
      )}
      {loadState === "error" && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-[#0a1628]/90 p-6 text-center text-sm text-red-400">
          {loadError || "Could not load the coverage map."}
        </div>
      )}
      <div
        ref={containerRef}
        className="relative z-0 h-full w-full"
        style={{ height: MAP_HEIGHT_PX }}
        aria-label="Coverage area map"
      />
    </div>
  );
}
